import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CompletionRequest, CompletionMessage } from '@app-libs/model';
import { StreamingApiClient } from '../client/streaming-api-client';
import { HttpClient } from '@angular/common/http';
import { ConfigurationClientService } from '../client/configuration-client.service';

@Injectable({
  providedIn: 'root',
})
export class CompletionApiService extends StreamingApiClient {
  private readonly basePath = '/completion'; // Unified completion endpoint

  constructor(
    protected override config: ConfigurationClientService,
    protected override http: HttpClient,
  ) {
    super(config, http);
  }

  override get baseUrl(): string {
    return this.config.getBackendAPIUrl() + this.basePath;
  }

  /**
   * Unified completion method that routes to any AI provider based on the model.
   * This calls the backend's unified completion endpoint which automatically:
   * - Detects the provider from the model name
   * - Routes to the appropriate provider service
   * - Returns streaming responses in standardized format
   *
   * Supports all providers: OpenAI, Gemini, Claude (when added)
   */
  createCompletion(request: CompletionRequest): Observable<any> {
    console.debug('CompletionApiService: Creating completion with request:', request);
    const url = `${this.baseUrl}`;
    // Always streaming for unified endpoint
    return this.createStreamingRequest(url, request);
  }

  /**
   * Creates a plain-text non-streaming completion using unified endpoint.
   * Routes to appropriate provider based on model name.
   */
  createCompletionAsText(
    prompt: string,
    messages: Partial<CompletionMessage>[],
    model: string,
  ): Observable<string> {
    const url = `${this.baseUrl}/text`;
    return this.http.post(url, { prompt, messages, model }, { responseType: 'text' });
  }

  /**
   * Creates a completion with image support using unified endpoint.
   * Routes to vision-capable providers (OpenAI GPT-4V, Gemini Pro Vision).
   */
  createImageCompletion(
    prompt: string,
    messages: Partial<CompletionMessage>[],
    model: string,
    image: File,
  ): Observable<string> {
    const url = `${this.baseUrl}/image`;
    const formData = new FormData();
    formData.append('prompt', prompt);
    formData.append('messages', JSON.stringify(messages));
    formData.append('model', model);
    formData.append('image', image);
    
    return this.http.post(url, formData, { responseType: 'text' });
  }

  /**
   * Transcribes audio using unified endpoint.
   * Routes to audio-capable providers (currently OpenAI Whisper).
   */
  transcribeAudio(audioBlob: Blob, provider = 'openai'): Observable<string> {
    console.debug('CompletionApiService: Transcribing audio with provider:', provider);
    const url = `${this.baseUrl}/audio/transcribe`;
    const formData = new FormData();
    const format = this.config.getSetting('fileUploads', 'audioCaptureFormat') || 'webm';
    formData.append('audioBlob', audioBlob, 'audio.' + format);
    formData.append('provider', provider);
    
    return this.http.post(url, formData, { responseType: 'text' });
  }

  /**
   * Uploads a file to AI provider using unified endpoint.
   * Routes to appropriate provider based on provider parameter.
   */
  uploadFile(file: File, provider = 'openai'): Observable<{ fileId: string; correlationId: string }> {
    console.debug('CompletionApiService: Uploading file to provider:', provider, file.name, file.type);
    const url = `${this.baseUrl}/files/upload`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('provider', provider);
    
    return this.http.post<{ fileId: string; correlationId: string }>(url, formData);
  }

  /**
   * Helper method to get available providers and models.
   * Uses the completion-providers endpoint to get current provider status.
   */
  getAvailableProviders(): Observable<any> {
    const url = `${this.config.getBackendAPIUrl()}/providers/available`;
    return this.http.get(url);
  }
}